import { FC } from 'react'
import { formatDate } from '@/core/utils/time'
import { TTheme, useCustomTheme } from '@/core/theme'
import Typography from '@/ui/components/typography'
import WarningTriangleIcon from '@assets/images/warning-triangle.svg?react'
import {
    CardBody,
    CardHeader,
    CardWrapper,
    MetaRow,
    OverflowChip,
    ReasonRow,
} from './FdEmailDetailsCard.styles'
import { FdEmailDetailsCardProps } from './FdEmailDetailsCard.types'

const MAX_VISIBLE_RECIPIENTS = 3

const FdEmailDetailsCard: FC<FdEmailDetailsCardProps> = ({
    emailData,
    recipients = [],
}) => {
    const theme = useCustomTheme() as TTheme
    const { subject, date_sent, sender, reasons } = emailData

    const visibleRecipients = recipients.slice(0, MAX_VISIBLE_RECIPIENTS)
    const overflowCount = recipients.length - visibleRecipients.length

    return (
        <CardWrapper data-testid="fd-email-details-card">
            <CardHeader>
                <WarningTriangleIcon
                    width={16}
                    height={16}
                    color={theme.colors.reds[600]}
                />
                <Typography
                    variant="s-semibold"
                    color={theme.colors.greys[800]}
                >
                    Emails detected as suspicious
                </Typography>
            </CardHeader>
            <CardBody>
                <MetaRow>
                    <Typography
                        variant="xs-bold"
                        className="key"
                        color={theme.colors.greys[800]}
                    >
                        Subject:
                    </Typography>
                    <Typography
                        variant="xs-regular"
                        noWrap={false}
                        color={theme.colors.greys[800]}
                        testId="drawer-fd-issue-details-email-subject"
                    >
                        {subject}
                    </Typography>
                </MetaRow>
                <MetaRow>
                    <Typography
                        variant="xs-bold"
                        className="key"
                        color={theme.colors.greys[800]}
                    >
                        Date:
                    </Typography>
                    <Typography
                        variant="xs-regular"
                        color={theme.colors.greys[800]}
                        testId="drawer-fd-issue-details-email-date"
                    >
                        {formatDate(date_sent, 'dd MMM yyyy HH:mm')}
                    </Typography>
                </MetaRow>
                <MetaRow>
                    <Typography
                        variant="xs-bold"
                        className="key"
                        color={theme.colors.greys[800]}
                    >
                        From:
                    </Typography>
                    <Typography
                        variant="xs-regular"
                        color={theme.colors.greys[800]}
                        testId="drawer-fd-issue-details-email-from-name"
                    >
                        {sender.name}
                    </Typography>
                    <Typography
                        variant="xs-regular"
                        color={theme.colors.greys[800]}
                        testId="drawer-fd-issue-details-email-from-address"
                    >
                        {`(${sender.email_address})`}
                    </Typography>
                </MetaRow>
                <MetaRow>
                    <Typography
                        variant="xs-bold"
                        className="key"
                        color={theme.colors.greys[800]}
                    >
                        To:
                    </Typography>
                    <Typography
                        variant="xs-regular"
                        noWrap={false}
                        color={theme.colors.greys[800]}
                        testId="drawer-fd-issue-details-email-recipients"
                    >
                        {visibleRecipients
                            .map(({ emailAddress }) => emailAddress)
                            .join(', ')}
                    </Typography>
                    {overflowCount > 0 && (
                        <OverflowChip data-testid="fd-recipients-overflow">
                            <Typography variant="xs-semibold" color="inherit">
                                {`+${overflowCount}`}
                            </Typography>
                        </OverflowChip>
                    )}
                </MetaRow>
                {reasons && reasons.length > 0 && (
                    <Typography
                        variant="s-regular"
                        color={theme.colors.greys[800]}
                    >
                        This email is flagged as suspicious due to:
                    </Typography>
                )}
                {reasons?.map((reason: Reason, reasonIndex: number) => (
                    <ReasonRow
                        key={`${reason.name}-${reasonIndex}`}
                        data-testid="drawer-fd-issue-details-reason"
                    >
                        <div className="reason-title">
                            <WarningTriangleIcon
                                width={14}
                                height={14}
                                color={theme.colors.reds[600]}
                            />
                            <Typography
                                variant="xs-bold"
                                color={theme.colors.greys[800]}
                                testId="drawer-fd-issue-details-reason-name"
                            >
                                {reason.name}
                            </Typography>
                        </div>
                        <Typography
                            variant="xs-regular"
                            noWrap={false}
                            color={theme.colors.greys[500]}
                            testId="drawer-fd-issue-details-reason-description"
                        >
                            {reason.description}
                        </Typography>
                    </ReasonRow>
                ))}
            </CardBody>
        </CardWrapper>
    )
}

export default FdEmailDetailsCard
